import { useQuery } from '@tanstack/react-query';
import chunk from 'lodash/chunk';
import { useSelector } from 'react-redux';

import { getExperienceOfferById, getExperienceOffers } from '~/services/ExperiencesService';
import { rethrowAbortError, throwIfAborted } from '~/services/common';

import { useNormalizedIds } from '../useNormalizedIds';

import { experiencesQueryKeys } from './experiencesQueryKeys';

const FALLBACK_CONCURRENCY = 5;

export function useGetExperienceOffersByIds(ids: Array<string>) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const normalizedIds = useNormalizedIds(ids);

  const query = useQuery({
    queryKey: [...experiencesQueryKeys.offers(), 'by-ids', brand, normalizedIds],
    queryFn: async ({ signal }) => {
      const response = await getExperienceOffers({
        brand: brand.toLowerCase(),
        curationData: true,
        limit: normalizedIds.length,
        filters: { idIn: normalizedIds.join(',') },
        signal,
      });
      const offersById = new Map(response.result.map((offer) => [offer.id, offer]));
      const missingIds = normalizedIds.filter((id) => !offersById.has(id));
      for (const group of chunk(missingIds, FALLBACK_CONCURRENCY)) {
        throwIfAborted(signal);
        await Promise.all(
          group.map(async (id) => {
            try {
              const response = await getExperienceOfferById({
                id,
                brand: brand.toLowerCase(),
                curationData: true,
                signal,
              });
              offersById.set(id, response.result);
            } catch (error) {
              rethrowAbortError(error, signal);
            }
          }),
        );
      }
      return offersById;
    },
    enabled: normalizedIds.length > 0 && !!brand,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    isError: query.isError || (!!query.data && normalizedIds.some((id) => !query.data.has(id))),
  };
}
